import { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import { userTypesList } from '../lists'
import { userType } from '../types'
import { useLocation, useNavigate } from 'react-router-dom'
import { dataContext } from '../index'
import {
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  TableSortLabel,
  Button,
  Chip,
  TablePagination,
  ListItemText,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Drawer,
  TextField,
} from '@mui/material'
import { Add, Close as CloseIcon } from '@mui/icons-material'
import './Users.css'
import ErrorScreen from '../Misc/ErrorScreen'
import AddUserDrawer from './AddUserDrawer'
import UserDetailsDrawer from './UserDetailsDrawer'
import { getRepCodeName } from '../helper'

const getRoleLabel = (role: string): string => {
  if (role === 'Administrator') return 'Admin'
  return role
}

function Users() {
  const { salesReps } = useContext(dataContext)

  const [users, setUsers] = useState<userType[]>([])
  const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>([])
  const [selectedUsers, setSelectedUsers] = useState<userType[]>([])
  //const [salesReps, setSalesReps] = useState<(salesRepType & { user_id: string; primary_flag: boolean })[]>([])

  const [sortField, setSortField] = useState<string>('display_name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [sortedUsers, setSortedUsers] = useState<userType[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [addUserOpen, setAddUserOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [selectedUser, setSelectedUser] = useState<userType | null>(null)
  const [userDetailsOpen, setUserDetailsOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<{ show: boolean; userName: string }>({ show: false, userName: '' })
  const [addUserError, setAddUserError] = useState<{ show: boolean; displayName: string }>({
    show: false,
    displayName: '',
  })
  const [editUserError, setEditUserError] = useState<{ show: boolean; displayName: string }>({
    show: false,
    displayName: '',
  })

  const [deleteSalesRepError, setDeleteSalesRepError] = useState<{ show: boolean; count: number }>({
    show: false,
    count: 0,
  })

  const [addSalesRepError, setAddSalesRepError] = useState<{ show: boolean; displayName: string }>({
    show: false,
    displayName: '',
  })

  const { user } = useContext(dataContext)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    document.title = 'Users | Salestrack'
  }, [])

  useEffect(() => {
    if (!user.user_type) {
      return
    }

    if (user.user_type !== 'Administrator') {
      navigate('/')
    }
  }, [user, navigate])

  useEffect(() => {
    if (location.state?.userType) {
      setSelectedUserTypes([location.state.userType])
      search([location.state.userType])
    } else {
      // Show all users by default by selecting all user types
      const allUserTypes = ['Administrator', 'Operations', 'Sales', 'Read Only']
      setSelectedUserTypes(allUserTypes)
      search(allUserTypes)
    }
  }, [location.state?.userType])

  useEffect(() => {
    // Apply sorting
    const sorted = [...users].sort((a, b) => {
      const aValue = a[sortField as keyof typeof a]
      const bValue = b[sortField as keyof typeof b]

      // Handle null/undefined values
      if (aValue === null) return sortDirection === 'asc' ? -1 : 1
      if (bValue === null) return sortDirection === 'asc' ? 1 : -1

      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    setSortedUsers(sorted)
    // Reset to first page when data changes
    setPage(0)
  }, [sortField, sortDirection, users])

  const search = (userTypes: string[]) => {
    if (userTypes.length === 0) {
      setUsers([])
      return
    }

    // Instead of making multiple requests, make a single request with all user types
    axios
      .get(`/users`, { params: { userTypes: userTypes } })
      .then(response => {
        // Map old role names to new role names in the response
        const mappedUsers = response.data.map((user: userType) => ({
          ...user,
          user_type: mapOldRoleToNewRole(user.user_type),
        }))

        setUsers(mappedUsers)
        setLoadError(false)
      })
      .catch(error => {
        console.error('Error fetching users:', error)
        setUsers([])
        setLoadError(true)
      })
  }

  const mapOldRoleToNewRole = (oldRole: string): string => {
    switch (oldRole) {
      case 'Operation Staff':
        return 'Operations'
      case 'Sales Rep':
        return 'Sales'
      case 'Office Staff':
        return 'Read Only'
      case 'Administrator':
        return 'Administrator'
      default:
        return oldRole
    }
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleDelete = () => {
    if (selectedUsers.length === 0) {
      alert('Please select users to delete')
      return
    }
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    const deletePromises = selectedUsers.map(user => axios.delete(`/deleteUser/${user.user_id}`))

    Promise.all(deletePromises)
      .then(() => {
        setUsers(users.filter(user => !selectedUsers.some(selected => selected.user_id === user.user_id)))
        setSelectedUsers([])
        setDeleteDialogOpen(false)
      })
      .catch(error => {
        console.error('Error deleting users:', error)
        setDeleteDialogOpen(false)
        setDeleteError({
          show: true,
          userName: selectedUsers.length === 1 ? selectedUsers[0].display_name ?? '' : `${selectedUsers.length} users`,
        })
      })
  }

  const handleUserTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as unknown as string[]
    setSelectedUserTypes(value)
    if (value.length > 0) {
      search(value)
    } else {
      setUsers([])
    }
  }

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Get paginated data
  const paginatedUsers = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <div className="users-container">
      <div className="user-search-container">
        <TextField
          select
          label="Roles"
          value={selectedUserTypes}
          onChange={handleUserTypeChange}
          size="small"
          fullWidth
          slotProps={{
            select: {
              multiple: true,
              renderValue: selected => (
                <div className="roles-chip-container">
                  {(selected as string[]).map(value => (
                    <Chip
                      key={value}
                      label={getRoleLabel(value)}
                      size="small"
                      className={`role-chip-${value.toLowerCase().replace(' ', '-')}`}
                    />
                  ))}
                </div>
              ),
            },
          }}
        >
          {userTypesList.map(type => (
            <MenuItem key={type} value={type}>
              <Checkbox size="small" checked={selectedUserTypes.indexOf(type) > -1} />
              <ListItemText primary={getRoleLabel(type)} />
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<Add />} onClick={() => setAddUserOpen(true)}>
          Add User
        </Button>
      </div>
      <Divider />
      {loadError ? (
        <div className="users-error-container">
          <ErrorScreen
            errorText="We couldn't load the user list. Please try again or contact support if the problem persists."
            title="User List Load Failed"
          />
        </div>
      ) : (
        <div id="list" className="content">
          <TableContainer className="users-table">
            <div className="users-table-header-container">
              <div className="users-table-actions">
                <Typography variant="subtitle1" color="text.primary">
                  {selectedUsers.length} Selected
                </Typography>
                <Button variant="outlined" onClick={handleDelete} disabled={selectedUsers.length === 0}>
                  Delete
                </Button>
              </div>
              <TablePagination
                component="div"
                count={sortedUsers.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
            <Table stickyHeader color="primary">
              <TableHead>
                <TableRow>
                  <TableCell width="50px" />
                  <TableCell className="users-cell users-display-name-cell" onClick={() => handleSort('display_name')}>
                    <TableSortLabel
                      active={sortField === 'display_name'}
                      direction={sortDirection}
                      onClick={() => handleSort('display_name')}
                    >
                      Display Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="users-cell users-role-cell" onClick={() => handleSort('user_type')}>
                    <TableSortLabel
                      active={sortField === 'user_type'}
                      direction={sortDirection}
                      onClick={() => handleSort('user_type')}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="users-cell users-qb-sales-rep-cell" onClick={() => handleSort('fullname')}>
                    <TableSortLabel
                      active={sortField === 'fullname'}
                      direction={sortDirection}
                      onClick={() => handleSort('fullname')}
                    >
                      QB Sales Rep
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="users-cell users-email-cell" onClick={() => handleSort('email')}>
                    <TableSortLabel
                      active={sortField === 'email'}
                      direction={sortDirection}
                      onClick={() => handleSort('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map(user => (
                  <TableRow key={user.user_id} className="users-table-row">
                    <TableCell>
                      <Checkbox
                        checked={selectedUsers.some(selected => selected.user_id === user.user_id)}
                        onChange={(_, checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, user])
                          } else {
                            setSelectedUsers(selectedUsers.filter(selected => selected.user_id !== user.user_id))
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="users-cell users-display-name-cell">
                      <Link
                        href="#"
                        onClick={e => {
                          e.preventDefault()
                          setSelectedUser(user)
                          setUserDetailsOpen(true)
                        }}
                      >
                        {user.display_name}
                      </Link>
                    </TableCell>
                    <TableCell className="users-cell users-role-cell">
                      <Chip
                        label={getRoleLabel(user.user_type)}
                        size="small"
                        className={`role-chip role-chip-${user.user_type.toLowerCase().replace(' ', '-')}`}
                      />
                    </TableCell>
                    <TableCell className="users-cell users-qb-sales-rep-cell">
                      {user.user_type === 'Sales'
                        ? getRepCodeName(
                            salesReps.find(
                              rep =>
                                [rep.inside_sales_rep, rep.account_executive, rep.engineer].includes(user.email) &&
                                rep.active,
                            ),
                          )
                        : '-'}
                    </TableCell>
                    <TableCell className="users-cell users-email-cell">{user.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              className="users-table-pagination-footer"
              component="div"
              count={sortedUsers.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </div>
      )}
      <Drawer anchor="right" open={addUserOpen} onClose={() => setAddUserOpen(false)}>
        <AddUserDrawer
          onUserAdded={() => search(selectedUserTypes)}
          onError={setAddUserError}
          onClose={() => setAddUserOpen(false)}
        />
      </Drawer>
      <Drawer
        anchor="right"
        open={userDetailsOpen}
        onClose={() => {
          setUserDetailsOpen(false)
          setSelectedUser(null)
        }}
      >
        <UserDetailsDrawer
          selectedUser={selectedUser}
          onUserUpdated={updatedUser => {
            // Update the local users state with the edited user data
            setUsers(prevUsers => prevUsers.map(user => (user.user_id === updatedUser.user_id ? updatedUser : user)))
            // Also update the selectedUser state to reflect changes
            setSelectedUser(updatedUser)

            // Refresh the data to ensure everything is in sync
            search(selectedUserTypes)
          }}
          onError={setEditUserError}
          onClose={() => {
            setUserDetailsOpen(false)
            setSelectedUser(null)
          }}
        />
      </Drawer>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Delete User{selectedUsers.length > 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            CANCEL
          </Button>
          <Button onClick={confirmDelete} variant="contained">
            DELETE USER{selectedUsers.length > 1 ? 'S' : ''}
          </Button>
        </DialogActions>
      </Dialog>
      <div className="users-error-alerts-container">
        {addUserError.show && (
          <Alert
            severity="error"
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setAddUserError({ show: false, displayName: '' })
                  }}
                >
                  RETRY
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setAddUserError({ show: false, displayName: '' })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <AlertTitle>Add User Failed</AlertTitle>
            An error occurred while adding user "{addUserError.displayName}". Please try again or contact support if the
            error persists.
          </Alert>
        )}
        {deleteError.show && (
          <Alert
            severity="error"
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setDeleteError({ show: false, userName: '' })
                    confirmDelete()
                  }}
                >
                  RETRY
                </Button>
                <IconButton color="inherit" size="small" onClick={() => setDeleteError({ show: false, userName: '' })}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <AlertTitle>Delete Failed</AlertTitle>
            An error occurred while deleting {deleteError.userName}. Please try again or contact support if the error
            persists.
          </Alert>
        )}
        {editUserError.show && (
          <Alert
            severity="error"
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setEditUserError({ show: false, displayName: '' })
                  }}
                >
                  RETRY
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setEditUserError({ show: false, displayName: '' })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <AlertTitle>Edit User Failed</AlertTitle>
            An error occurred while updating user "{editUserError.displayName}". Please try again or contact support if
            the error persists.
          </Alert>
        )}
        {deleteSalesRepError.show && (
          <Alert
            severity="error"
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setDeleteSalesRepError({ show: false, count: 0 })
                  }}
                >
                  RETRY
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setDeleteSalesRepError({ show: false, count: 0 })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <AlertTitle>Delete Sales Rep Failed</AlertTitle>
            An error occurred while deleting {deleteSalesRepError.count} sales rep
            {deleteSalesRepError.count !== 1 ? 's' : ''}. Please try again or contact support if the error persists.
          </Alert>
        )}
        {addSalesRepError.show && (
          <Alert
            severity="error"
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setAddSalesRepError({ show: false, displayName: '' })
                  }}
                >
                  RETRY
                </Button>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setAddSalesRepError({ show: false, displayName: '' })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <AlertTitle>Add Sales Rep Failed</AlertTitle>
            An error occurred while adding sales rep for user "{addSalesRepError.displayName}". Please try again or
            contact support if the error persists.
          </Alert>
        )}
      </div>
    </div>
  )
}

export default Users
